import { z } from "zod";

export const financialThresholdsSchema = z.object({
  negativeAtOrBelow: z.number().finite().min(-1000000).max(-0.01).multipleOf(0.01),
  positiveAtOrAbove: z.number().finite().min(0.01).max(1000000).multipleOf(0.01),
}).strict();
export type FinancialThresholds = z.infer<typeof financialThresholdsSchema>;
export const DEFAULT_FINANCIAL_THRESHOLDS: FinancialThresholds = {
  negativeAtOrBelow: -0.01, positiveAtOrAbove: 0.01,
};
export type FinancialField = "balance" | "financial_status" | "current_dues" | "on_time_payment_rate" | "last_payment";
export type FinancialState = "unknown" | "unverified" | "unavailable" | "invalid" | "stale" | "observed";
export interface FinancialDatum {
  expiresAt?: string;
  state: FinancialState;
  display: string;
  reason: string;
  observedAt: string | null;
}
export const FINANCIAL_READ_BATCH_SIZE = 20;

/** A failed refresh is not evidence of a zero balance or a missing observation. */
export function retainFinancialEvidence(member: MemberFinancials, reason: string): MemberFinancials {
  const fields = { ...member.fields };
  for (const field of FINANCIAL_FIELDS) {
    const datum = fields[field];
    if (datum.state === "observed" || datum.state === "stale") {
      fields[field] = { ...datum, state: "stale", reason: `${reason} Last successful observation retained. ${datum.reason}` };
    } else if (datum.state === "unknown" || datum.state === "unavailable") {
      fields[field] = { ...datum, state: "unavailable", display: "Temporarily unavailable", reason };
    }
  }
  return { ...member, fields, classification: "unknown", readStatus: "failed" };
}

export function expireFinancialEvidence(member: MemberFinancials, now = Date.now()): MemberFinancials {
  const fields = { ...member.fields };
  for (const field of FINANCIAL_FIELDS) {
    const datum = fields[field];
    if (datum.state === "observed" && datum.expiresAt && Date.parse(datum.expiresAt) <= now) {
      fields[field] = { ...datum, state: "stale", reason: `Observation is older than the accepted age limit. ${datum.reason}` };
    }
  }
  return { ...member, fields, classification: fields.balance.state === "observed" ? member.classification : "unknown" };
}
export interface MemberFinancials {
  readStatus?: "success" | "failed" | "cache";
  memberId: number;
  fields: Record<FinancialField, FinancialDatum>;
  classification: "positive" | "zero-band" | "negative" | "unknown";
  automation: { enabled: false; reason: string };
}
export interface FinancialSettings {
  account: FinancialThresholds;
  override: FinancialThresholds | null;
  effective: FinancialThresholds;
  source: "property" | "account" | "default";
  canEdit: boolean;
  properties: { id: number; name: string }[];
}
export const FINANCIAL_FIELDS: FinancialField[] = [
  "balance", "financial_status", "current_dues", "on_time_payment_rate", "last_payment",
];
export function unknownFinancials(memberId: number, reason: string): MemberFinancials {
  return {
    memberId,
    fields: Object.fromEntries(FINANCIAL_FIELDS.map(field => [
      field, { state: "unknown", display: "Unknown", reason, observedAt: null },
    ])) as Record<FinancialField, FinancialDatum>,
    classification: "unknown",
    automation: { enabled: false, reason: "Financial observation messaging is disabled." },
  };
}

/** Display/configuration is credit-positive; canonical totalDue is debt-positive. */
export function classifyFinancialBalance(totalDue: number | null, settings: FinancialThresholds): MemberFinancials["classification"] {
  if (totalDue === null || !Number.isFinite(totalDue)) return "unknown";
  const creditPositive = -totalDue;
  if (creditPositive <= settings.negativeAtOrBelow) return "negative";
  if (creditPositive >= settings.positiveAtOrAbove) return "positive";
  return "zero-band";
}
